/* SFLT-PDF Intake Kit — shared JS
 * Exposes window.SfltPdf with subscribe(), openLightbox(),
 * openBundleModal(), smoothScrollTo(). Auto-runs on DOMContentLoaded.
 */
(function () {
  'use strict';

  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const PAGE_LOAD_TS = Date.now();

  function getConfig() {
    return (window.__sfltPdfConfig || {});
  }

  const SfltPdf = {
    /**
     * Subscribe an email to a Klaviyo list with optional consent flag.
     * @param {string} email
     * @param {{listId: string, consentMarketing: boolean, assetName: string}} opts
     * @returns {Promise<void>} resolves on 202; rejects on validation/HTTP error.
     */
    async subscribe(email, opts) {
      const cfg = getConfig();
      if (!cfg.companyId || !/^[A-Za-z0-9]{6}$/.test(cfg.companyId)) {
        throw new Error('Klaviyo companyId missing or malformed in window.__sfltPdfConfig');
      }
      if (!opts || !opts.listId) {
        throw new Error('listId required');
      }
      if (!this._isValidEmail(email)) {
        throw new Error('invalid email');
      }

      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'SFLT-PDF Intake Kit',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  properties: {
                    intake_consent_marketing: !!opts.consentMarketing,
                    intake_asset_requested: opts.assetName || ''
                  }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: opts.listId } }
          }
        }
      };

      const url = KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(cfg.companyId);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'revision': KLAVIYO_REVISION,
          'content-type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (res.status !== 202) {
        const text = await res.text().catch(() => '');
        throw new Error('Klaviyo subscribe failed: ' + res.status + ' ' + text);
      }
    },

    openLightbox(imageUrl, alt) {
      if (!imageUrl) return;
      let dialog = document.getElementById('sflt-pdf-lightbox');
      if (!dialog) {
        dialog = document.createElement('dialog');
        dialog.id = 'sflt-pdf-lightbox';
        dialog.className = 'sflt-pdf-dialog sflt-pdf-armor';
        dialog.innerHTML =
          '<button type="button" aria-label="Close preview" ' +
          'style="position:absolute;top:8px;right:8px;background:transparent;' +
          'border:0;color:var(--sflt-fg1);font-size:20px;padding:8px;cursor:pointer;z-index:1" ' +
          'data-sflt-pdf-close>✕</button>' +
          '<img alt="" style="display:block;width:100%;height:auto;max-height:calc(100vh - 64px);object-fit:contain;background:var(--sflt-bg)">';
        document.body.appendChild(dialog);
        // Close on backdrop click (e.target === dialog when clicking the backdrop)
        // or on the close button.
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog) dialog.close();
          if (e.target.matches('[data-sflt-pdf-close]')) dialog.close();
        });
      }
      const img = dialog.querySelector('img');
      img.src = imageUrl;
      img.alt = alt || '';
      dialog.showModal();
    },

    openBundleModal() {
      const dialog = document.getElementById('sflt-pdf-bundle-modal');
      if (!dialog) {
        console.warn('[SfltPdf] bundle modal not found on page');
        return;
      }
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      }
    },

    closeBundleModal() {
      const dialog = document.getElementById('sflt-pdf-bundle-modal');
      if (dialog && dialog.open) dialog.close();
    },

    smoothScrollTo(target) {
      const el = typeof target === 'string' ? document.querySelector(target) : target;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    /** Used internally to gate fast/empty submissions. Exposed for tests. */
    _isLikelyHuman(formEl) {
      // Honeypot: any non-empty .sflt-pdf-honeypot input → bot.
      const hp = formEl.querySelector('.sflt-pdf-honeypot input');
      if (hp && hp.value.trim() !== '') return false;
      // Time gate: page-load < 1s ago → bot.
      if (Date.now() - PAGE_LOAD_TS < 1000) return false;
      return true;
    },

    _isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    },

    _getConfig: getConfig,

    async _handleFormSubmit(form) {
      const successEl = form.querySelector('[data-sflt-pdf-success]');
      const errorEl = form.querySelector('[data-sflt-pdf-error]');
      const button = form.querySelector('button[type="submit"]');
      const emailInput = form.querySelector('input[type="email"]');
      const consentInput = form.querySelector('input[data-sflt-pdf-consent]');
      const listId = form.dataset.sfltPdfListId;
      const assetName = form.dataset.sfltPdfAssetName || '';
      const successMessage = form.dataset.sfltPdfSuccessMessage || '';
      const fakeSuccess = () => this._showSuccess(form, emailInput.value, successMessage);

      if (errorEl) errorEl.textContent = '';

      // Honeypot + time gate. On bot signals, fake-success without API call.
      if (!this._isLikelyHuman(form)) { fakeSuccess(); return; }

      const email = emailInput.value.trim();
      if (!this._isValidEmail(email)) {
        if (errorEl) errorEl.textContent = 'VALID EMAIL REQUIRED';
        return;
      }

      const originalLabel = button ? button.innerHTML : '';
      if (button) {
        button.disabled = true;
        button.dataset.sfltPdfOriginalLabel = originalLabel;
        button.textContent = 'DEPLOYING...';
      }

      try {
        await this.subscribe(email, {
          listId,
          consentMarketing: !!(consentInput && consentInput.checked),
          assetName
        });
        // After successful lead capture, trigger the file download.
        // Bot/honeypot paths intentionally don't download (handled above in fakeSuccess).
        const downloadUrl = form.dataset.sfltPdfDownloadUrl;
        if (downloadUrl) this._triggerDownload(downloadUrl);
        this._showSuccess(form, email, successMessage);
      } catch (err) {
        if (errorEl) errorEl.textContent = 'SUBMIT FAILED — TRY AGAIN';
        console.error('[SfltPdf]', err);
        if (button) {
          button.disabled = false;
          button.innerHTML = button.dataset.sfltPdfOriginalLabel || originalLabel;
        }
      }
    },

    /**
     * Triggers a file download by synthesizing a hidden <a download href="...">,
     * clicking it, and removing it. Cross-origin URLs (like Shopify CDN) won't
     * honor the `download` attribute — the browser falls back to opening the
     * URL, which for PDFs typically shows the browser's PDF viewer in a new tab.
     * Either outcome delivers the file to the user.
     */
    _triggerDownload(url) {
      if (!url) return;
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      a.target = '_blank';
      a.setAttribute('download', '');
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    _showSuccess(form, email, message) {
      const wrap = form.closest('[data-sflt-pdf-form-wrap]') || form.parentElement;
      const successEl = wrap.querySelector('[data-sflt-pdf-success]');
      if (successEl) {
        const tokenized = (message || 'CHECK YOUR INBOX TO CONFIRM.')
          .replace('[email]', email);
        // Prefer a dedicated text target so structural children (icons, headlines)
        // aren't wiped. Fall back to the success element itself.
        const textTarget = successEl.querySelector('[data-sflt-pdf-success-text]') || successEl;
        textTarget.textContent = tokenized;
        successEl.hidden = false;
      }
      form.hidden = true;
    },

    init() {
      if (window.SfltPdf && window.SfltPdf.__initialized) return;
      this.__initialized = true;

      document.addEventListener('submit', (ev) => {
        const form = ev.target.closest('[data-sflt-pdf-form]');
        if (!form) return;
        ev.preventDefault();
        SfltPdf._handleFormSubmit(form);
      });

      document.addEventListener('click', (ev) => {
        const previewBtn = ev.target.closest('[data-sflt-pdf-preview]');
        if (previewBtn) {
          ev.preventDefault();
          SfltPdf.openLightbox(
            previewBtn.dataset.sfltPdfPreview,
            previewBtn.dataset.sfltPdfAlt || ''
          );
          return;
        }

        const bundleBtn = ev.target.closest('[data-sflt-pdf-open-bundle]');
        if (bundleBtn) {
          ev.preventDefault();
          SfltPdf.openBundleModal();
          return;
        }

        const closeBtn = ev.target.closest('[data-sflt-pdf-close-bundle]');
        if (closeBtn) {
          ev.preventDefault();
          SfltPdf.closeBundleModal();
          return;
        }

        const scrollBtn = ev.target.closest('[data-sflt-pdf-scroll-to]');
        if (scrollBtn) {
          ev.preventDefault();
          SfltPdf.smoothScrollTo(scrollBtn.dataset.sfltPdfScrollTo);
          return;
        }
      });
    },
  };

  window.SfltPdf = SfltPdf;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => SfltPdf.init());
  } else {
    SfltPdf.init();
  }
})();
